import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Mail, ArrowLeft, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center p-8">
      {/* Home logo */}
      <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 hover:opacity-80 transition" data-testid="forgot-home-btn">
        <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
        <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
      </button>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#242424] rounded-2xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {sent ? 'Check Your Email' : 'Forgot Password'}
            </h2>
            <p className="text-neutral-500 mt-2">
              {sent
                ? 'If an account with that email exists, a password reset link has been sent.'
                : 'Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {sent ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400">
                <Check className="w-5 h-5" />
                <span>Reset instructions sent</span>
              </div>
              <button
                data-testid="back-to-login-btn"
                onClick={() => navigate('/login')}
                className="w-full py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                  <input
                    type="email"
                    data-testid="forgot-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <button
                type="button"
                data-testid="back-to-login-link"
                onClick={() => navigate('/login')}
                className="w-full text-center text-neutral-500 hover:text-orange-400 transition text-sm flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
